import { Component, Inject, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { constants, ILabelValueConstant } from 'app/constants/constants';
import { GlobalService } from 'app/global/global.service';
import { HttpActionState } from 'app/main/pages/users/userslisting/store/users-listing.reducers';
import { BehaviorSubject, catchError, combineLatest, debounceTime, filter, finalize, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { ICostForm, ICostRevenueConversionRates, ITotalCost } from '../../contract-control-detail-view/cc-cost/cost.dto';
import { Cost } from '../../contract-control.dto';
import { ContractControlService } from '../../contract-control.service';
import { ContractControlFacade } from '../../store/facades/contract-control-facade';
import { ContractControlPermissionService } from '../../contract-control.permission.service';
import { CONTRACT_DETAIL_FACADE_TOKEN, IContractDetailFacade } from '../../contract-control-detail-view/store/contract-detail-facade.token';
import { FlatpickrDirective } from 'angularx-flatpickr';
import { HttpErrorResponse } from '@angular/common/http';
import flatpickr from 'flatpickr';
export enum ActionEnum {
  CREATING_CONTRACT = 'creating_contract',
  EDITING_CONTRACT = 'editing_contract',
}

@Component({
  selector: 'app-add-cost-modal',
  templateUrl: './add-cost-modal.component.html',
  styleUrls: ['./add-cost-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddCostModalComponent implements OnInit, OnDestroy {

  @ViewChild('billingEndPicker', { static: false }) billingEndPicker: FlatpickrDirective;
  @Input() cost: Cost;
  @Input() document_type_id: number;
  @Input() contract_control_id: number;
  @Input() hasPermission$: Observable<boolean> = of(false);
  @Input() actionFrom: ActionEnum = ActionEnum.EDITING_CONTRACT;


  public costConversationRates: ICostRevenueConversionRates;
  public submitted: boolean = false;
  public loading: boolean = false;
  private isLoadingConversionRates: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private conversionSuccessful: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private contractControlService: ContractControlService,
    private globalService: GlobalService,
    private _translocoService: TranslocoService,
    private _contractControlFacade: ContractControlFacade,
    private _contractControlPermissionService: ContractControlPermissionService,
    @Inject(CONTRACT_DETAIL_FACADE_TOKEN) public _contractFacade: IContractDetailFacade
  ) { }

  public chooseCurrency: ILabelValueConstant[] = constants.contract_control_currencies;
  public systemCurrency: string;
  public canEditContractCard$: Observable<boolean> = of(false);
  public hasFullAccess$: Observable<boolean> = of(false);
  public costForm: FormGroup;
  public totalCost: ITotalCost;
  private actionQueue: BehaviorSubject<string> = new BehaviorSubject<string>('');
 
  public billingFrequencyOptions: Array<ILabelValueConstant> = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Semi-Annually', value: 'semi_annually' },
    { label: 'Annually', value: 'annually' },
    { label: 'Every two years', value: 'two_years' },
    { label: 'Every three years', value: 'three_years' },
    { label: 'Every five years', value: 'five_years' },
    { label: 'One-time', value: 'one_time' }
  ];



  ngOnInit(): void {
    this.systemCurrency = this.globalService.getSystemCurrency();
    this.initCostForm(this.cost);


    this._contractFacade.selectedContract$.pipe(
      filter(x => !!x),
      take(1),
    ).subscribe(contract => {
      this.canEditContractCard$ = this._contractControlPermissionService.canEditContract(contract.document_type_id, contract.document_type_id, contract.category_ids, contract);
      this.hasFullAccess$ = this._contractControlPermissionService.hasFullAccess(contract.document_type_id, contract.document_type_id, contract.category_ids, contract);
    });
  }





  /**
   * Initializes the reactive form for cost data entry and editing.
   *
   * @param data - A partial object containing initial values for the cost form fields.
   *               If a field is not provided, a default value is used.
   *
   * The form includes the following controls:
   * - `id`: The unique identifier of the cost.
   * - `document_type_id`: The associated document type ID.
   * - `contract_control_id`: The associated contract control ID.
   * - `description`: A description of the cost.
   * - `cost_amount`: The monetary amount of the cost.
   * - `billing_frequency`: The frequency at which billing occurs.
   * - `billing_start`: The start date or marker for billing.
   * - `cost_currency`: The currency of the cost.
   * - `billing_end`: The end date or marker for billing.
   * - `has_billing_end_date`: Indicates if there is a billing end date.
   *
   * The form is set to update on blur events. On any value change, the `updateCost` method is triggered.
   * 
   * @remarks
   * - The method sets up the form with default values if data is missing.
   * - Value changes are subscribed to for triggering cost updates.
   * - There are commented sections for future improvements, such as debouncing and permission-based control enabling/disabling.
   */
  initCostForm(data: Partial<Cost | null>): void {
    this.costForm = this.formBuilder.group({
      id: [data?.id],
      document_type_id: [this.document_type_id, Validators.required],
      contract_control_id: [this.contract_control_id, Validators.required],
      description: [data?.description, Validators.required],
      cost_amount: [data?.cost_amount, [Validators.required, Validators.min(0)]],
      billing_frequency: [data?.billing_frequency, [Validators.required]],
      billing_start: [data?.billing_start, [Validators.required]],
      cost_currency: [data?.cost_currency ?? this.systemCurrency],
      billing_end: [data?.billing_end],
      has_billing_end_date: [data?.has_billing_end_date ?? false],
      cost_converted_amount: [data?.cost_converted_amount ?? null],
      conversion_rate: [data?.conversion_rate ?? null],
      conversion_date: [data?.conversion_date ?? null],
    }, { validators: [this.startBeforeEndValidator] });

    this.costForm.get('cost_amount')?.valueChanges.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.conversionRates()
    });



    this.costForm.get('has_billing_end_date')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
    ).subscribe((hasBillingEndDate) => {
      this.disabledBillingEnd(!hasBillingEndDate);
    });

    this.costForm.get('cost_currency').valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.conversionRates();
    });


    // Disabled form controls if user has no permission
    this.disableFormControls();
    

    this.removeValidatorsIfCreatingContract();
  }


  /**
   * Removes the `Validators.required` validator from the `contract_control_id` and `document_type_id`
   * form controls if the current action is `CREATING_CONTRACT`.
   * 
   * This method is useful for dynamically adjusting form validation rules based on the current action.
   * After removing the validators, it updates the validity state of the affected controls.
   *
   * @private
   */
  private removeValidatorsIfCreatingContract(): void {
    if(this.actionFrom === ActionEnum.CREATING_CONTRACT){
      this.costForm.get('contract_control_id')?.removeValidators([Validators.required]);
      this.costForm.get('contract_control_id')?.updateValueAndValidity();

      this.costForm.get('document_type_id')?.removeValidators([Validators.required]);
      this.costForm.get('document_type_id')?.updateValueAndValidity();
    }
  }




  /** 
   * Handles changes to the billing start date input field.
   * This method updates the `billing_start` form control in the `costForm` FormGroup
   * based on the value from the input event. If the input value is empty,
   * it sets the control's value to `null`; otherwise, it sets it to the input value.
   *
   * @param event - The input event from the billing start date field.
   */
  onBillingStartDateChange(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value;
    this.costForm.get('billing_start')?.setValue(value || null, {emitEvent: false});
  }




  /** 
   * Handles changes to the billing end date input field.
   *
   * This method updates the `billing_end` form control in the `costForm` FormGroup
   * based on the value from the input event. If the input value is empty,
   * it sets the control's value to `null`; otherwise, it sets it to the input value.
   *
   * @param event - The input event from the billing end date field.
   */
  onBillingEndDateChange(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value;
    this.costForm.get('billing_end')?.setValue(value || null, {emitEvent: false});
  }





  /**
   * Enables or disables the 'billing_end' form control in the costForm.
   * 
   * This method resets the value of the 'billing_end' control, then enables or disables it
   * based on the provided parameter. After changing the state, it updates the control's
   * value and validity.
   *
   * @param shouldDisabled - If true, disables the 'billing_end' control; otherwise, enables it. Defaults to false.
   */
  private disabledBillingEnd(shouldDisabled: boolean = false): void {
    const billingEndControl = this.costForm.get('billing_end');

    if (shouldDisabled) {
      const inst: flatpickr.Instance = this.billingEndPicker?.instance;
      if (inst && typeof inst.clear === 'function') {
        inst.clear();
      }
      billingEndControl?.setValue(null, { emitEvent: false });
      billingEndControl?.disable({ emitEvent: false });
    } else {
      billingEndControl?.enable({ emitEvent: false });
    }

    billingEndControl?.updateValueAndValidity({ emitEvent: false });
  }





  /**
   * Disables or enables all controls in the `costForm` form group based on the user's permission.
   * 
   * Subscribes to the `hasPermission$` observable and, upon receiving the first value,
   * iterates through each control in the form. If the user does not have permission,
   * all controls are disabled; otherwise, they are enabled.
   *
   * @private
   */
  private disableFormControls(): void {
    this._contractFacade.selectedContract$.pipe(
      take(1),
      filter(x => !!x),
      switchMap(contract =>
        this._contractControlPermissionService.canEditContract(
          contract.document_type_id,
          contract.document_type_id,
          contract.category_ids,
          contract
        )
      ),
      take(1)
    ).subscribe(canEdit => {
      Object.values(this.costForm.controls).forEach(control =>
        canEdit ? control.enable() : control.disable()
      );
    });
  }




  /**
   * Custom validator to ensure that the start date is before the end date in a form group.
   *
   * This validator checks the values of 'billing_start' and 'billing_end' controls within the provided form group.
   * If both dates are present and the start date is after the end date, it returns a validation error object.
   * If the dates are valid or not both present, it returns null, indicating no validation errors.
   * @param formGroup - The FormGroup containing 'billing_start' and 'billing_end' controls to be validated.
   * @returns A validation error object if the start date is after the end date, otherwise null.
   */
  private startBeforeEndValidator(formGroup: FormGroup) {
    const start = formGroup.get('billing_start')?.value;
    const end = formGroup.get('billing_end')?.value;
    if (start && end && new Date(start) > new Date(end)) {
      return { startAfterEnd: true };
    }
    return null;
  }




  get form() { return this.costForm.controls; }



  
   /**
   * Handles the update of cost information by submitting the form data.
   * 
   * - Sets the `submitted` flag to true to indicate the form is being processed.
   * - Validates the form; if invalid, the method returns early.
   * - Calls the facade's `updateCost` method with the form values to initiate the update.
   * - Subscribes to the `updateCostRequestState$` observable to listen for completion or failure of the update request.
   *   - On completion or failure, resets the `submitted` flag, clears the form, and closes the modal.
   *
   * @remarks
   * This method replaces the previous direct service call with a facade-based approach,
   * and manages UI state based on the request's outcome.
   */
  updateCost(): void {
    this.submitted = true;
    if (this.costForm.invalid) return;

    if(this.actionFrom === ActionEnum.CREATING_CONTRACT){
      this.closeModal(this.costForm.value);
      return;
    }
    this._contractFacade.updateCost(this.costForm.value);

    // Listen for the updateCostRequestState$ to determine when the update is done
    this._contractFacade.updateCostRequestState$.pipe(
      filter(x => x === HttpActionState.Done),
      take(1)
    ).subscribe(() => {
      this.submitted = false;
      this.costForm.reset();
      this.closeModal(null);
    });
  }




  /**
   * Closes the modal dialog without returning any data.
   *
   * Calls the `close` method on the `activeModal` instance, passing `null` as the result.
   */
  closeModal(costForm: ICostForm): void {
    this.activeModal.close(costForm);
  }



  /**
   * Observable that emits a boolean indicating whether a cost deletion request is currently in progress.
   * 
   * @returns {Observable<boolean>} Emits `true` if the delete cost request state is `Busy`, otherwise `false`.
   */
  get isDeleting$(): Observable<boolean> {
    return this._contractFacade.deleteCostRequestState$.pipe(
      take(1),
      map(state => state === HttpActionState.Busy)
    );
  }



  /**
   * Deletes a cost entry by its ID.
   *
   * This method waits for the `deleteCostRequestState$` observable to emit a state
   * that is not `HttpActionState.Busy`, ensuring that no other delete operation is in progress.
   * Once the state is ready, it triggers the deletion of the cost with the specified ID
   * via the `_contractControlFacade` service and then closes the modal dialog.
   *
   * @param id - The unique identifier of the cost entry to be deleted.
   */
  deleteCost(id: number): void {
    this._contractFacade.deleteCostRequestState$.pipe(take(1)).subscribe(state => {
        if (state === HttpActionState.Busy) {
          return;
        }

      this._contractFacade.deleteCost(id);

      this._contractFacade.deleteCostRequestState$.pipe(
        takeUntil(this.destroy$),
        filter(s => s === HttpActionState.Done || s === HttpActionState.Failed),
        take(1)
      ).subscribe(() => {
        this.closeModal(null);
      });
    });
  }



  get isCostLoading$(): Observable<boolean> {
    return this._contractFacade.updateCostRequestState$.pipe(
      take(1),
      map(state => state === HttpActionState.Busy)
    );
  }

  get isLoadingConversionRates$(): Observable<boolean> {
    return this.isLoadingConversionRates.asObservable();
  }

  get isSaveDisabled$(): Observable<boolean> {
    return combineLatest([
      this.isCostLoading$,
      this.isLoadingConversionRates$,
      this.conversionSuccessful.asObservable()
    ]).pipe(
      map(([isSaving, isLoadingConversion, conversionSuccess]) => 
        isSaving || isLoadingConversion || !conversionSuccess
      )
    );
  }


  /**
   * Handles currency conversion for the current cost object.
   *
   * If the selected currency is different from the system currency and the amount is valid,
   * this method calls the contractControlService to fetch the conversion rate and updates
   * the cost object with the converted amount, conversion rate, and conversion date.
   * Displays a success toast message upon successful conversion.
   * If the currency is the same as the system currency or the amount is invalid,
   * it resets the converted amount, conversion rate, and conversion date to default values.
   *
   * @remarks
   * This method relies on external services for currency conversion and user notifications.
   *
   * @returns {void}
   */
  conversionRates(): void {
    if (this.actionFrom === ActionEnum.CREATING_CONTRACT) {
      this.costForm.get('cost_converted_amount')?.setValue(null);
      this.costForm.get('conversion_rate')?.setValue(null);
      this.costForm.get('conversion_date')?.setValue(null);
      this.conversionSuccessful.next(true);
      return;
    }

    const selectedCurrency = this.costForm.get('cost_currency')?.value;
    const amount = this.costForm.get('cost_amount')?.value;
    
    if (selectedCurrency != this.systemCurrency && amount != null && amount != 0) {
      this.isLoadingConversionRates.next(true);
      this.conversionSuccessful.next(false);
      this.contractControlService.conversionRates(selectedCurrency, amount as number, this.contract_control_id, this.costForm.get('id')?.value, 'cost').pipe(
        tap((response) => {
          this.costForm.get('cost_converted_amount')?.setValue(response.cost_converted_amount);
          this.costForm.get('conversion_rate')?.setValue(response.conversion_rate);
          this.costForm.get('conversion_date')?.setValue(response.conversion_date);
          this.conversionSuccessful.next(true);
          this.globalService.toastrSuccess(this._translocoService.translate('Update cost currency success toast message - (Contract control section) - Converted price has been updated'));
        }),
        catchError((error: HttpErrorResponse) => {
          this.globalService.toastrError(error.error.message || this._translocoService.translate('Update cost currency error toast message - (Contract control section) - There was an error while updating the converted price'));
          this.conversionSuccessful.next(false);
          return of(null);
        }),
        finalize(() => this.isLoadingConversionRates.next(false)),
        takeUntil(this.destroy$)
      ).subscribe();
    }
    else {
        this.costForm.get('cost_converted_amount')?.setValue(null);
        this.costForm.get('conversion_rate')?.setValue(null);
        this.costForm.get('conversion_date')?.setValue(null);
        this.conversionSuccessful.next(true);
    }
  }



  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
